"use client"

import { useState } from "react"
import { addStaff } from "../../../services/AdminService"
import { ToastContainer, toast } from "react-toastify"
import { FaUser, FaEnvelope, FaPhone, FaUserPlus, FaCheckCircle, FaTimesCircle } from "react-icons/fa"
import "react-toastify/dist/ReactToastify.css"

const sanitizeNameInput = (value) =>
  String(value || "")
    .replace(/[^a-zA-Z\s]/g, "")
    .replace(/\s{2,}/g, " ")
    .replace(/^\s+/, "")

const AddStaff = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [touched, setTouched] = useState({})

  const validateField = (field, value) => {
    let error = ""

    switch (field) {
      case "name":
        if (!value.trim()) {
          error = "Name is required"
        } else if (value.trim().length < 2) {
          error = "Name must be at least 2 characters"
        } else if (value.trim().length > 50) {
          error = "Name must be less than 50 characters"
        } else if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          error = "Name can only contain letters and spaces"
        }
        break

      case "email":
        if (!value.trim()) {
          error = "Email is required"
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          error = "Please enter a valid email address"
        }
        break

      case "phone":
        if (!value.trim()) {
          error = "Phone number is required"
        } else if (!/^[6-9]\d{9}$/.test(value.trim())) {
          error = "Please enter a valid 10-digit mobile number starting with 6-9"
        }
        break

      default:
        break
    }

    return error
  }

  const validateForm = () => {
    const newErrors = {}
    Object.keys(formData).forEach((field) => {
      const error = validateField(field, formData[field])
      if (error) newErrors[field] = error
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    let nextValue = value
    if (field === "name") {
      nextValue = sanitizeNameInput(value)
    }
    if (field === "phone") {
      nextValue = String(value || "").replace(/\D/g, "").slice(0, 10)
    }

    setFormData((prev) => ({ ...prev, [field]: nextValue }))

    // Real-time validation
    if (touched[field]) {
      const error = validateField(field, nextValue)
      setErrors((prev) => ({ ...prev, [field]: error }))
    }
  }

  const handleInputBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }))
    const error = validateField(field, formData[field])
    setErrors((prev) => ({ ...prev, [field]: error }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Mark all fields as touched
    setTouched({ name: true, email: true, phone: true })

    if (!validateForm()) {
      toast.error("Please fix the validation errors before submitting.")
      return
    }

    setLoading(true)
    const clientId = sessionStorage.getItem("clientId")

    if (!clientId) {
      toast.error("Session expired! Please login again.")
      setLoading(false)
      return
    }

    const staffData = {
      p_action: "I",
      p_user_id: 0,
      p_user_name: formData.name.trim(),
      p_email: formData.email.trim(),
      p_mobile: formData.phone.trim(),
      p_profile_pic: "default_profile.jpg",
      p_status: 0, // Set to active by default
      p_token: "autogeneratedtoken123",
      p_otp: "123456",
      p_is_verified: 0,
      p_is_available: 1,
      P_pannumber: "ABCDE1234F",
      p_DocPath: "/uploads/default_docs",
      p_role_abbreviation: "S",
      p_ClientId: clientId,
      p_page_size: 0,
      p_page: 0,
    }

    try {
      const response = await addStaff(staffData)
      console.log("✅ Staff Added:", response)

      if (response.status === "success" || response.success) {
        toast.success("🎉 Staff member added successfully!")
        // Reset form
        setFormData({ name: "", email: "", phone: "" })
        setErrors({})
        setTouched({})
      } else {
        toast.error(response.message || "Failed to add staff member.")
      }
    } catch (error) {
      console.error("❌ Failed to add staff:", error)

      if (error.response?.status === 409) {
        toast.error("Staff member with this email or phone already exists!")
      } else if (error.response?.status === 400) {
        toast.error("Invalid data provided. Please check your inputs.")
      } else {
        toast.error("Failed to add staff member. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({ name: "", email: "", phone: "" })
    setErrors({})
    setTouched({})
    toast.info("Form reset successfully.")
  }

  const getFieldStatus = (field) => {
    if (!touched[field]) return ""
    return errors[field] ? "is-invalid" : "is-valid"
  }

  return (
    <div className="container-fluid py-4 add-staff-page">
      <ToastContainer position="top-right" autoClose={4000} hideProgressBar />

      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm add-staff-hero">
            <div className="card-body">
              <div className="d-flex align-items-center">
                <FaUserPlus className="me-3 fs-2 add-staff-hero-icon" />
                <div>
                  <h4 className="mb-1">Add Staff Member</h4>
                  <p className="mb-0 text-muted">Create a staff profile with validated contact details.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Form */}
      <div className="row justify-content-center">
        <div className="col-lg-8 col-md-10 col-12">
          <div className="card border-0 shadow-sm add-staff-card">
            <div className="card-header bg-transparent border-0 pt-4 px-4 pb-0">
              <h5 className="mb-0">
                <FaUser className="me-2" />
                Staff Information
              </h5>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSubmit} noValidate>
                <div className="row">
                  {/* Name Field */}
                  <div className="col-md-12 mb-4">
                    <label className="form-label fw-semibold">
                      <FaUser className="me-2 text-primary" />
                      Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control modern-input ${getFieldStatus("name")}`}
                      placeholder="Enter full name"
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      onBlur={() => handleInputBlur("name")}
                      disabled={loading}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    {!errors.name && touched.name && (
                      <div className="valid-feedback">
                        <FaCheckCircle className="me-1" />
                        Looks good!
                      </div>
                    )}
                    <div className="form-text">
                      <small>Use 2-50 characters with letters and spaces only.</small>
                    </div>
                  </div>

                  {/* Email Field */}
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold">
                      <FaEnvelope className="me-2 text-primary" />
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      className={`form-control modern-input ${getFieldStatus("email")}`}
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      onBlur={() => handleInputBlur("email")}
                      disabled={loading}
                    />
                    {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    {!errors.email && touched.email && (
                      <div className="valid-feedback">
                        <FaCheckCircle className="me-1" />
                        Valid email format!
                      </div>
                    )}
                    <div className="form-text">
                      <small>This will be used for login and notifications</small>
                    </div>
                  </div>

                  {/* Phone Field */}
                  <div className="col-md-6 mb-4">
                    <label className="form-label fw-semibold">
                      <FaPhone className="me-2 text-primary" />
                      Mobile Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="tel"
                      className={`form-control modern-input ${getFieldStatus("phone")}`}
                      placeholder="Enter 10-digit mobile number"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      onBlur={() => handleInputBlur("phone")}
                      maxLength="10"
                      disabled={loading}
                    />
                    {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                    {!errors.phone && touched.phone && (
                      <div className="valid-feedback">
                        <FaCheckCircle className="me-1" />
                        Valid mobile number!
                      </div>
                    )}
                    <div className="form-text">
                      <small>Enter a valid Indian mobile number (starting with 6-9)</small>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="row mt-4">
                  <div className="col-12">
                    <div className="d-flex justify-content-center gap-3">
                      <button
                        type="submit"
                        className="btn btn-primary btn-lg px-5 rounded-pill"
                        disabled={loading || Object.keys(errors).some((key) => errors[key])}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Adding Staff...
                          </>
                        ) : (
                          <>
                            <FaUserPlus className="me-2" />
                            Add Staff Member
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-lg px-4 rounded-pill"
                        onClick={handleReset}
                        disabled={loading}
                      >
                        <FaTimesCircle className="me-2" />
                        Reset Form
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Information Card */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm add-staff-guidelines">
            <div className="card-header bg-transparent">
              <h6 className="mb-0">
                <FaCheckCircle className="me-2 text-success" />
                Quick Guidelines
              </h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h6 className="text-primary mb-3">Default Settings</h6>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <FaCheckCircle className="text-success me-2" />
                      New staff will be created with <strong>Active</strong> status
                    </li>
                    <li className="mb-2">
                      <FaCheckCircle className="text-success me-2" />
                      Default role will be assigned as <strong>Staff</strong>
                    </li>
                    <li className="mb-2">
                      <FaCheckCircle className="text-success me-2" />
                      Email verification will be required for first login
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6 className="text-primary mb-3">Validation Rules</h6>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <FaCheckCircle className="text-success me-2" />
                      Name: 2-50 characters, letters and spaces only
                    </li>
                    <li className="mb-2">
                      <FaCheckCircle className="text-success me-2" />
                      Email: Must be a valid email format
                    </li>
                    <li className="mb-2">
                      <FaCheckCircle className="text-success me-2" />
                      Mobile: 10-digit Indian number (6-9 prefix)
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .add-staff-page {
          --staff-border: #dbe4f0;
          --staff-surface: #ffffff;
          background:
            radial-gradient(circle at 4% 0%, rgba(14, 165, 233, 0.14), transparent 28%),
            radial-gradient(circle at 96% 25%, rgba(37, 99, 235, 0.12), transparent 30%),
            #f4f8ff;
          border-radius: 18px;
        }
        .add-staff-hero {
          border: 1px solid var(--staff-border);
          background: linear-gradient(145deg, #ffffff 0%, #f8fbff 100%);
        }
        .add-staff-hero-icon {
          color: #1d4ed8;
        }
        .add-staff-card {
          border: 1px solid var(--staff-border);
          background: var(--staff-surface);
        }
        .modern-input {
          min-height: 48px;
          border-radius: 12px;
          border-color: #d4deea;
          box-shadow: none;
        }
        .modern-input:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.12);
        }
        .add-staff-guidelines {
          border: 1px solid var(--staff-border);
          background: #ffffff;
        }
      `}</style>
    </div>
  )
}

export default AddStaff
